import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { reportServerVersion } from "./version-banner";

// JWT token storage for Safari/iframe fallback
const TOKEN_KEY = "auth_token";

// Endpoints that support the ?warehouseId=<id> filter. When an admin has
// selected a specific warehouse via the WarehouseFilterProvider, the URL is
// rewritten to include the query parameter automatically.
const WAREHOUSE_AWARE_URL_PREFIXES = [
  "/api/orders",
  "/api/stats",
  // «Trenger deg naa» leser de samme ordrene som /api/stats teller. Uten
  // filteret her ville lista vist andre lagre enn kortene rett over den.
  "/api/dashboard/next-steps",
  "/api/returns/tracking",
  "/api/shipments/tracking",
  "/api/preparation/stats",
  "/api/playback-devices",
];

/** Delt med WarehouseFilterProvider -- filteret maa leses FOER React rekker
 *  aa montere, ellers rekker de foerste spoerringene aa gaa ufiltrert ut. */
export const WAREHOUSE_FILTER_STORAGE_KEY = "warehouse_filter";

function readPersistedWarehouseFilter(): string | null {
  try {
    const stored = localStorage.getItem(WAREHOUSE_FILTER_STORAGE_KEY);
    return stored && stored !== "all" ? stored : null;
  } catch {
    return null;
  }
}

// Primes synkront ved modullasting. Settes den foerst i en useEffect (som
// tidligere), rekker dashbordets spoerringer aa gaa ut uten ?warehouseId --
// og da viser tallene ALLE lagre mens velgeren sier «Snarøya».
let currentWarehouseFilter: string | null = readPersistedWarehouseFilter();

export function setCurrentWarehouseFilter(warehouseId: string | null): void {
  currentWarehouseFilter = warehouseId && warehouseId !== "all" ? warehouseId : null;
}

/** Spoerringene som avhenger av lagerfilteret (brukes til invalidering). */
export function isWarehouseAwareQueryKey(key: unknown): boolean {
  if (typeof key !== "string") return false;
  return (
    key === "/api/orders" ||
    key.startsWith("/api/orders/") ||
    key === "/api/stats" ||
    key.startsWith("/api/stats/") ||
    key === "/api/returns/tracking" ||
    key === "/api/shipments/tracking" ||
    key.startsWith("/api/preparation")
  );
}

export function getCurrentWarehouseFilter(): string | null {
  return currentWarehouseFilter;
}

function applyWarehouseFilter(url: string): string {
  if (!currentWarehouseFilter) return url;
  const path = url.split("?")[0];
  const matches = WAREHOUSE_AWARE_URL_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + "/"),
  );
  if (!matches) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}warehouseId=${encodeURIComponent(currentWarehouseFilter)}`;
}

// Global session expired handler - set by SessionProvider
let onSessionExpired: (() => void) | null = null;

// Grace period after login: don't show "session expired" modal for 401 during this time
// (avoids double login when cookie/JWT isn't applied yet on the very next request)
const LOGIN_GRACE_MS = 4000;
let lastLoginAt = 0;

export function setSessionExpiredHandler(handler: (() => void) | null) {
  onSessionExpired = handler;
}

/** Call after successful login so we don't show session-expired modal for a short period. */
export function setJustLoggedIn() {
  lastLoginAt = Date.now();
}

function handleUnauthorized() {
  if (Date.now() - lastLoginAt < LOGIN_GRACE_MS) {
    console.log("[queryClient] Ignoring 401 during login grace period");
    return;
  }
  console.log("[queryClient] Handling 401 unauthorized, onSessionExpired handler:", onSessionExpired ? "set" : "not set");
  if (onSessionExpired) {
    onSessionExpired();
  } else {
    console.warn("[queryClient] No session expired handler set - modal won't show");
  }
}

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // localStorage not available
  }
}

async function throwIfResNotOk(res: Response, triggerSessionModal = true) {
  if (!res.ok) {
    if (res.status === 401 && triggerSessionModal) {
      handleUnauthorized();
    }
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Build headers with optional JWT token for Safari fallback
function buildHeaders(data?: unknown): HeadersInit {
  const headers: HeadersInit = {};
  if (data) {
    headers["Content-Type"] = "application/json";
  }
  // Add JWT token if available (Safari/iframe fallback)
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const finalUrl = method === "GET" ? applyWarehouseFilter(url) : url;
  const res = await fetch(finalUrl, {
    method,
    headers: buildHeaders(data),
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  reportServerVersion(res.headers.get("X-App-Version"));
  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const rawUrl = queryKey.join("/") as string;
    const res = await fetch(applyWarehouseFilter(rawUrl), {
      credentials: "include",
      headers: buildHeaders(),
    });

    reportServerVersion(res.headers.get("X-App-Version"));

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    if (res.status === 401) {
      handleUnauthorized();
    }

    await throwIfResNotOk(res, false);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Primære arbeidslister skal hente ferske data når maskinen kommer på nett
// igjen eller brukeren vender tilbake til fanen. Detalj- og skjemaspørringer
// beholder den konservative globale standarden for å unngå tap av lokalt arbeid.
export const LIVE_LIST_QUERY_OPTIONS = {
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  staleTime: 60_000,
} as const;

// Helper function for authenticated file downloads (PDFs, etc.)
// Returns true if download succeeded, false if auth failed (and triggers session modal)
export async function downloadAuthenticatedFile(
  url: string, 
  filename: string
): Promise<boolean> {
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: buildHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorized();
      return false;
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    // Get the blob and create download
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Foretrekk serverens filnavn (Content-Disposition) slik at navnekonvensjonen
    // bor ETT sted (server); `filename`-parameteret er kun fallback.
    const disposition = response.headers.get("Content-Disposition") || "";
    let serverFilename: string | undefined;
    const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (star) {
      try { serverFilename = decodeURIComponent(star[1].trim()); } catch { /* behold fallback */ }
    }
    if (!serverFilename) {
      const plain = disposition.match(/filename="?([^";]+)"?/i);
      if (plain) serverFilename = plain[1].trim();
    }

    // Create a temporary link and click it to trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = serverFilename || filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up blob URL
    URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    console.error("[download] Error downloading file:", error);
    throw error;
  }
}
